import { Task, User } from '../types';
import { Clock, User as UserIcon, Trash2, Edit2 } from 'lucide-react';
import { getTaskOrder } from '../utils/taskFiltering';
import { getPriorityColors, getTaskTypeColors } from '../constants/colorScheme';

interface TaskCardProps {
  task: Task;
  users: User[];
  onClick: () => void;
  canEdit?: boolean;
  canDelete?: boolean;
  onEdit?: (task: Task) => void;
  onDelete?: (taskId: string) => void;
}

export default function TaskCard({
  task,
  users,
  onClick,
  canEdit = false,
  canDelete = false,
  onEdit,
  onDelete,
}: TaskCardProps) {
  const taskOrder = getTaskOrder(task);

  const assignees = users?.filter((u) => task.assigneeIds?.includes(u.id)) || [];
  const totalHours = task.timeLogs?.reduce((sum, log) => sum + log.hours, 0) || 0;

  return (
    <div
      onClick={onClick}
      className="group cursor-pointer rounded-lg border border-border bg-card px-3 py-2.5 text-card-foreground transition-shadow hover:shadow-md hover:border-border/80"
    >
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className={`rounded border px-1.5 py-0.5 text-[10px] font-medium leading-none ${getTaskTypeColors(task.type).badge}`}>
            {task.type}
          </span>
          {taskOrder !== null && (
            <span className="rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] leading-none text-muted-foreground">
              #{taskOrder}
            </span>
          )}
        </div>
        <span className={`text-[11px] font-medium leading-none ${getPriorityColors(task.priority).text}`}>
          {task.priority}
        </span>
      </div>

      <h4 className="line-clamp-2 text-sm font-medium leading-snug text-foreground">{task.title}</h4>

      {task.description && (
        <p className="mt-1 line-clamp-2 text-xs leading-snug text-muted-foreground">{task.description}</p>
      )}

      <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
        <div className="flex items-center gap-1">
          <Clock className="h-3.5 w-3.5" />
          <span>{task.estimatedDays}d</span>
          {totalHours > 0 && <span className="text-[11px]">({totalHours}h)</span>}
        </div>

        <div className="flex items-center gap-2">
          {(task.subtaskIds?.length || 0) > 0 && (
            <span className="text-[11px]">{task.subtaskIds.length} sub</span>
          )}
          {(task.parentTaskIds?.length || 0) > 0 && (
            <span className="text-[11px]">{task.parentTaskIds.length} par</span>
          )}
          {assignees.length > 0 && (
            <div className="flex items-center gap-1">
              <UserIcon className="h-3.5 w-3.5" />
              <span>{assignees.length}</span>
            </div>
          )}
        </div>
      </div>

      {(canEdit || canDelete) && (
        <div className="mt-2.5 flex gap-2 border-t border-border pt-2">
          {canEdit && onEdit && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit(task);
              }}
              className="flex flex-1 items-center justify-center gap-1 rounded border border-primary/40 bg-primary/10 px-2 py-1 text-xs text-primary transition-colors hover:bg-primary/10"
            >
              <Edit2 className="h-3 w-3" />
              Edit
            </button>
          )}
          {canDelete && onDelete && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (confirm('Are you sure you want to delete this task?')) {
                  onDelete(task.id);
                }
              }}
              className="flex flex-1 items-center justify-center gap-1 rounded border border-destructive/40 bg-destructive/10 px-2 py-1 text-xs text-destructive transition-colors hover:bg-destructive/10"
            >
              <Trash2 className="h-3 w-3" />
              Delete
            </button>
          )}
        </div>
      )}
    </div>
  );
}